import { useTranslation } from "react-i18next";
import { useLangFont } from "../../../hooks/useLangFont";

const fixedPacks = [
  { size: "S", priceKey: "¥16,000", dimKey: "pricing_pack_size_s", featured: false },
  { size: "M", priceKey: "¥26,000", dimKey: "pricing_pack_size_m", featured: true },
  { size: "L", priceKey: "¥31,000", dimKey: "pricing_pack_size_l", featured: false },
];

const singlePrices = [
  "¥3,200〜", "¥3,200〜", "¥3,200〜", "¥2,500〜",
  "¥3,200〜", "¥3,200〜", "¥4,500〜", "¥4,500〜",
  "¥4,500〜", "¥1,800〜",
];

export default function ServicesAndPricing() {
  const { t } = useTranslation();
  const { heading, body } = useLangFont();

  const includes = [0, 1, 2, 3, 4, 5, 6].map((i) => t(`pricing_include_${i}`));
  const singleItems = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => ({
    name: t(`pricing_si_${i}`),
    price: singlePrices[i],
  }));

  return (
    <section id="pricing" className="py-28 bg-white w-full">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <span
            className="inline-block bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold tracking-[0.3em] uppercase px-4 py-2 rounded-full mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {t("pricing_label")}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-4 tracking-tight"
            style={{ fontFamily: heading }}
          >
            {t("pricing_title")}
          </h2>
          <p
            className="text-gray-500 text-base tracking-wide"
            style={{ fontFamily: body }}
          >
            {t("pricing_subtitle")}
          </p>
        </div>

        <div className="flex flex-col xl:flex-row gap-10 items-start">
          {/* Fixed Packs */}
          <div className="flex flex-col gap-5 w-full xl:w-2/5">
            <div className="flex items-center justify-between mb-2">
              <h3
                className="text-lg font-bold text-[#1A2332] tracking-widest"
                style={{ fontFamily: body }}
              >
                {t("pricing_pack_title")}
              </h3>
            </div>

            <div className="flex flex-wrap gap-2 -mt-2 mb-1">
              <span className="flex items-center gap-1.5 bg-[#C9A84C]/10 text-[#C9A84C] text-[11px] font-semibold px-3 py-1 rounded-full" style={{ fontFamily: body }}>
                <i className="ri-shield-check-line text-xs" />
                {t("pricing_badge_flat")}
              </span>
              <span className="flex items-center gap-1.5 bg-[#1A2332]/8 text-[#1A2332] text-[11px] font-semibold px-3 py-1 rounded-full" style={{ fontFamily: body }}>
                <i className="ri-price-tag-3-line text-xs" />
                {t("pricing_badge_no_extra")}
              </span>
            </div>

            {fixedPacks.map((pack) => (
              <div
                key={pack.size}
                className={`relative rounded-2xl p-7 transition-all duration-300 hover:scale-[1.02] cursor-default border-2 ${
                  pack.featured ? "bg-[#F8F7F4] border-[#C9A84C]" : "bg-[#F8F7F4] border-transparent"
                }`}
              >
                {pack.featured && (
                  <div
                    className="absolute -top-3 left-7 bg-[#C9A84C] text-[#1A2332] text-[10px] font-bold px-3 py-1 rounded-full tracking-widest uppercase"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {t("pricing_pack_popular")}
                  </div>
                )}
                <div className="flex items-center gap-4 mb-4">
                  <div
                    className="w-12 h-12 rounded-full bg-[#1A2332] flex items-center justify-center font-bold text-[#C9A84C] text-xl flex-shrink-0"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {pack.size}
                  </div>
                  <div>
                    <div
                      className="text-3xl font-bold tracking-tight text-[#1A2332]"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {pack.priceKey}
                    </div>
                    <div className="text-xs tracking-widest mt-0.5 text-gray-400" style={{ fontFamily: body }}>
                      {t(pack.dimKey)}
                    </div>
                  </div>
                </div>
                <ul className="flex flex-col gap-2 mb-5">
                  {includes.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm">
                      <i className="ri-check-line text-[#C9A84C] flex-shrink-0 w-4 h-4 flex items-center justify-center" />
                      <span className="text-gray-600" style={{ fontFamily: body }}>{item}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className={`btn-lift w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    pack.featured
                      ? "bg-[#C9A84C] text-[#1A2332] hover:bg-[#B8973B]"
                      : "bg-[#1A2332] text-white hover:bg-[#243246]"
                  }`}
                  style={{ fontFamily: body }}
                >
                  {t("pricing_pack_cta")}
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            ))}
          </div>

          {/* Single Menu — shifted down on desktop */}
          <div className="w-full xl:w-3/5 xl:mt-16 bg-[#F8F7F4] rounded-2xl p-8">
            <h3
              className="text-2xl font-bold text-[#1A2332] mb-1 tracking-tight"
              style={{ fontFamily: heading }}
            >
              {t("pricing_single_title")}
            </h3>
            <p
              className="text-gray-400 text-sm mb-6 tracking-wide"
              style={{ fontFamily: body }}
            >
              {t("pricing_single_subtitle")}
            </p>
            <div className="flex flex-col gap-0">
              {singleItems.map((item, index) => (
                <div
                  key={item.name}
                  className={`flex items-center justify-between px-4 py-3.5 border-b border-[#1A2332]/8 ${index === 0 ? "border-t border-[#1A2332]/8" : ""}`}
                >
                  <span
                    className="text-sm text-[#1A2332] tracking-wide"
                    style={{ fontFamily: body }}
                  >
                    {item.name}
                  </span>
                  <span
                    className="text-[#C9A84C] font-bold text-sm"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {item.price}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-[#1A2332]/5 rounded-xl flex items-start gap-3">
              <i className="ri-information-line text-[#C9A84C] text-lg w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" />
              <p
                className="text-xs text-gray-500 leading-relaxed"
                style={{ fontFamily: body }}
              >
                {t("pricing_note_1")}
              </p>
            </div>
            <div className="mt-3 p-4 rounded-xl flex items-start gap-3" style={{ background: "rgba(201,168,76,0.08)" }}>
              <i className="ri-information-line text-[#C9A84C] text-lg w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5" />
              <p
                className="text-xs text-gray-500 leading-relaxed"
                style={{ fontFamily: body }}
              >
                {t("pricing_note_2")}
              </p>
            </div>
          </div>
        </div>

        {/* XL note */}
        <div className="mt-8 rounded-2xl border border-[#C9A84C]/30 bg-[#C9A84C]/5 px-8 py-6 flex flex-col sm:flex-row items-center gap-5">
          <div className="w-14 h-14 rounded-full bg-[#1A2332] flex items-center justify-center flex-shrink-0">
            <i className="ri-luggage-cart-line text-[#C9A84C] text-2xl" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p
              className="text-[#1A2332] font-bold text-base mb-1 tracking-wide"
              style={{ fontFamily: heading }}
            >
              {t("pricing_xl_title")}
            </p>
            <p
              className="text-gray-500 text-sm leading-relaxed"
              style={{ fontFamily: body }}
            >
              {t("pricing_xl_desc")}
            </p>
          </div>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="flex-shrink-0 inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#243246] text-white text-sm font-bold px-6 py-3 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: body }}
          >
            {t("pricing_xl_cta")}
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}
